import type { SO_PartialListItem, SO_ShoppingList } from '@shared/domain/s-ostoslista'
import type { So_RemoteGetUserListsQuery } from '@shared/gql/sostoslista-document-nodes'

import { useQuery } from '@apollo/client'
import { So_RemoteGetUserListsDocument } from '@shared/gql/sostoslista-document-nodes'
import { remoteData } from 'domain/remote-data'
import { pipe } from 'fp-ts/function'
import { useMemo } from 'react'
import { useIsLoggedIn } from 'utils/hooks/account/use-is-logged-in'
import { isNonNullable } from 'utils/nullable'

import { shoppingListsQueryContext } from './shopping-lists-query-context'

export const useGetListsQuery = (storeId: string) => {
  const isLoggedIn = useIsLoggedIn()
  const remoteQuery = useQuery(So_RemoteGetUserListsDocument, {
    ...shoppingListsQueryContext,
    variables: {
      storeId,
    },
    skip: !isLoggedIn,
  })

  return useMemo(
    () =>
      pipe(
        remoteQuery,
        remoteData.from,
        remoteData.nonNullable,
        remoteData.map((x) => x.shoppingLists),
        remoteData.map((xs) => xs.map(mapToListType)),
      ),
    [remoteQuery],
  )
}

export const sortLists = (xs: SO_ShoppingList[]) =>
  [...xs].sort((a, b) => b.createdAt.localeCompare(a.createdAt))

const mapToListProductItem = (
  x: So_RemoteGetUserListsQuery['shoppingLists'][number]['items'][number],
): SO_PartialListItem | null => {
  if (x.__typename === 'ProductResolvedShoppingListItem') {
    return x
  }

  return null
}

const mapToListType = (
  x: So_RemoteGetUserListsQuery['shoppingLists'][number],
): SO_ShoppingList => ({
  ...x,
  items: x.items.map(mapToListProductItem).filter(isNonNullable),
})
